/**
 * Opening a claim.
 *
 * One dialog for both audiences, because it is one act: the customer opens a
 * claim from the portal, and the ADV opens the same claim when the customer
 * telephones instead — which, at first, is most of them. The difference is
 * one field: the desk says which customer, and the portal's server already
 * knows.
 *
 * The reason is a select rather than a text box because it is the field the
 * desk filters and reports on: "how many wrong references did we ship last
 * month" is a question free text cannot answer. Everything free text is good
 * for goes in the description.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Button, Dialog, Input, Select, Textarea, useToast } from "@hardauto/core-ui";

import type { NewTicket, Ticket, TicketReason } from "./api";
import { NAMESPACE } from "./i18n";
import { REASONS } from "./status";

export interface OpenTicketDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Sends the claim. Takes the customer id only on the desk's side, which is
   * why it is the caller that supplies the function rather than this dialog
   * choosing an endpoint.
   */
  onOpen: (draft: NewTicket & { client_id?: string }) => Promise<Ticket>;
  /** The desk names the customer; the portal's server already knows. */
  askForClient?: boolean;
  onOpened?: (ticket: Ticket) => void;
}

export function OpenTicketDialog({
  open,
  onClose,
  onOpen,
  askForClient = false,
  onOpened,
}: OpenTicketDialogProps) {
  const { t } = useTranslation(NAMESPACE);
  const action = useAction();
  const toast = useToast();

  const [clientId, setClientId] = useState("");
  const [reason, setReason] = useState<TicketReason>("warranty");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [orderReference, setOrderReference] = useState("");
  const [articleReference, setArticleReference] = useState("");
  const [quantity, setQuantity] = useState("");
  const [error, setError] = useState<string | null>(null);

  // A fresh claim each time it opens. Reopening it carrying the last
  // description is how a customer sends the same complaint twice.
  useEffect(() => {
    if (!open) return;
    setClientId("");
    setReason("warranty");
    setSubject("");
    setDescription("");
    setOrderReference("");
    setArticleReference("");
    setQuantity("");
    setError(null);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("open.title")}
      description={t("open.description")}
      closeLabel={t("open.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("open.cancel")}</Button>
          <Button
            variant="primary"
            busy={action.isBusy("open")}
            busyLabel={t("open.submit")}
            onClick={() =>
              void action.run("open", async () => {
                setError(null);
                if (!subject.trim() || !description.trim()) return;
                if (askForClient && !clientId.trim()) return;
                try {
                  const ticket = await onOpen({
                    reason,
                    subject: subject.trim(),
                    description: description.trim(),
                    order_reference: orderReference.trim(),
                    article_reference: articleReference.trim(),
                    quantity: quantity.trim() ? Number(quantity) : 0,
                    ...(askForClient ? { client_id: clientId.trim() } : {}),
                  });
                  toast.show(t("open.done", { reference: ticket.reference }), {
                    tone: "success",
                  });
                  onOpened?.(ticket);
                  onClose();
                } catch {
                  setError(t("open.failed"));
                }
              })
            }
          >
            {t("open.submit")}
          </Button>
        </>
      }
    >
      <div className="hardauto-form">
        {askForClient ? (
          <Field label={t("open.client")} hint={t("open.clientHint")} required>
            {(props) => (
              <Input
                {...props}
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              />
            )}
          </Field>
        ) : null}

        <Field label={t("open.reason")} required>
          {(props) => (
            <Select
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value as TicketReason)}
            >
              {REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`reason.${value}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t("open.subject")} required>
          {(props) => (
            <Input
              {...props}
              placeholder={t("open.subjectPlaceholder")}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("open.descriptionLabel")} required error={error ?? undefined}>
          {(props) => (
            <Textarea
              {...props}
              rows={4}
              placeholder={t("open.descriptionPlaceholder")}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("open.order")} hint={t("open.orderHint")}>
          {(props) => (
            <Input
              {...props}
              value={orderReference}
              onChange={(event) => setOrderReference(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("open.article")}>
          {(props) => (
            <Input
              {...props}
              value={articleReference}
              onChange={(event) => setArticleReference(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("open.quantity")}>
          {(props) => (
            <Input
              {...props}
              type="number"
              min={0}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
